import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import Envelope from "../card/Envelope";
import useMessage from "../card/useMessage";
import GiftBox from "../gift/GiftBox";
import HeartBurst from "../gift/HeartBurst";
import Lightbox from "../gift/Lightbox";
import LoveAmbient from "../gift/LoveAmbient";
import PhotoCollage, { entranceStyles } from "../gift/PhotoCollage";
import usePhotoMonths, { preloadImages } from "../gift/usePhotoMonths";
import useViewport from "../gift/useViewport";
import { heartPath } from "../gift/hearts";
import "../gift/love.css";

const cardBack = process.env.PUBLIC_URL + "/card-back.jpg";
const cardBackPortrait = process.env.PUBLIC_URL + "/card-back-portrait.jpg";

const burstVariantByEntrance = { burst: "burst", rain: "rain", spiral: "spiral", heart: "burst" };
const giftHint = "tap for more my love";
const finaleRiseMilliseconds = 2300;
const layoutGlide = { layout: { duration: 0.9, ease: [0.33, 0, 0.2, 1] } };
const photoWaitLimitMilliseconds = 2600;

const waitForPhotos = (urls) =>
  Promise.race([preloadImages(urls), new Promise((resolve) => setTimeout(resolve, photoWaitLimitMilliseconds))]);

const initialJourney = {
  openedCount: 0,
  activeMonthIndex: null,
  isLidOpen: false,
  finaleStage: "none",
};

function ProgressHearts({ total, filled }) {
  return (
    <motion.div className="progress-hearts" role="img" aria-label={`${filled} of ${total}`} layout="position" transition={layoutGlide}>
      {Array.from({ length: total }, (_, index) => {
        const isFilled = index < filled;
        return (
          <motion.svg
            key={index}
            viewBox="0 0 24 24"
            className={`progress-heart${isFilled ? " is-filled" : ""}`}
            initial={false}
            animate={{ scale: isFilled ? [1, 1.45, 1] : 1 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <path d={heartPath} />
          </motion.svg>
        );
      })}
    </motion.div>
  );
}

const Home = () => {
  const months = usePhotoMonths();
  const message = useMessage();
  const viewport = useViewport();
  const giftRef = useRef(null);
  const finaleTimerRef = useRef(0);
  const openingRoundRef = useRef(null);
  const [journey, setJourney] = useState(initialJourney);
  const [giftOrigin, setGiftOrigin] = useState({ x: 0, y: 0 });
  const [lightbox, setLightbox] = useState(null);
  const [burst, setBurst] = useState(null);
  const [envelopePhase, setEnvelopePhase] = useState("back");
  const [hasReadLetter, setHasReadLetter] = useState(false);
  const [playRound, setPlayRound] = useState(0);

  const { openedCount, activeMonthIndex, isLidOpen, finaleStage } = journey;
  const monthTotal = months ? months.length : 3;
  const activeMonth = months && activeMonthIndex !== null ? months[activeMonthIndex] : null;
  const isFinale = finaleStage !== "none";
  const giftMode = isFinale ? "finale" : isLidOpen ? "open" : "idle";

  const preloadTarget = isLidOpen && activeMonthIndex === null && !isFinale ? null : openedCount;

  useEffect(() => {
    if (!months || preloadTarget === null) return;
    const upcoming = months[preloadTarget];
    if (upcoming) preloadImages(upcoming.photos);
    else preloadImages([cardBack, cardBackPortrait]);
  }, [months, preloadTarget]);

  useEffect(() => {
    if (!burst) return undefined;
    const timer = setTimeout(() => setBurst(null), 3800);
    return () => clearTimeout(timer);
  }, [burst]);

  useEffect(() => () => clearTimeout(finaleTimerRef.current), []);

  const measureGiftOrigin = () => {
    const element = giftRef.current;
    if (!element) return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const rect = element.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height * 0.55 };
  };

  const launchBurst = (variant, origin) => {
    setBurst({ key: Date.now(), variant, origin });
  };

  const handleGiftTap = () => {
    if (!months || activeMonthIndex !== null || isLidOpen || isFinale) return;
    const origin = measureGiftOrigin();
    setGiftOrigin(origin);

    if (openedCount < months.length) {
      const monthIndex = openedCount;
      const entranceStyle = entranceStyles[monthIndex % entranceStyles.length];
      const round = playRound;
      openingRoundRef.current = round;
      setJourney((current) => ({ ...current, isLidOpen: true, openedCount: monthIndex + 1 }));
      waitForPhotos(months[monthIndex].photos).then(() => {
        if (openingRoundRef.current !== round) return;
        openingRoundRef.current = null;
        setJourney((current) => (current.openedCount === monthIndex + 1 ? { ...current, activeMonthIndex: monthIndex } : current));
        launchBurst(burstVariantByEntrance[entranceStyle], origin);
      });
      return;
    }

    setJourney((current) => ({ ...current, isLidOpen: true, finaleStage: "rising" }));
    launchBurst("burst", origin);
    clearTimeout(finaleTimerRef.current);
    finaleTimerRef.current = setTimeout(() => {
      setJourney((current) => (current.finaleStage === "rising" ? { ...current, finaleStage: "card" } : current));
    }, finaleRiseMilliseconds);
  };

  const closeMonth = useCallback(() => {
    setLightbox(null);
    setJourney((current) => ({ ...current, activeMonthIndex: null }));
  }, []);

  const handleCollageExitComplete = () => {
    setJourney((current) => (current.activeMonthIndex === null ? { ...current, isLidOpen: false } : current));
  };

  const navigateLightbox = useCallback(
    (step) => {
      if (!activeMonth) return;
      const count = activeMonth.photos.length;
      setLightbox((current) =>
        current ? { index: (current.index + step + count) % count, direction: step } : current
      );
    },
    [activeMonth]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (lightbox) {
        if (event.key === "Escape") setLightbox(null);
        else if (event.key === "ArrowRight") navigateLightbox(1);
        else if (event.key === "ArrowLeft") navigateLightbox(-1);
        return;
      }
      if (activeMonth && event.key === "Escape") closeMonth();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightbox, activeMonth, navigateLightbox, closeMonth]);

  const handleEnvelopePhase = useCallback((phase) => {
    setEnvelopePhase(phase);
    if (phase === "reading") setHasReadLetter(true);
  }, []);

  const startOver = () => {
    clearTimeout(finaleTimerRef.current);
    openingRoundRef.current = null;
    setJourney(initialJourney);
    setLightbox(null);
    setBurst(null);
    setEnvelopePhase("back");
    setHasReadLetter(false);
    setPlayRound((round) => round + 1);
  };

  const showReplay = hasReadLetter && (envelopePhase === "back" || envelopePhase === "front");
  const progressFilled = openedCount + (isFinale ? 1 : 0);
  const hint = months ? giftHint : "";

  return (
    <MotionConfig reducedMotion="user">
      <div
        className={`love-page${isFinale ? " is-finale" : ""}${activeMonth ? " is-collage" : ""}${lightbox ? " is-lightbox" : ""}`}
      >
        <LoveAmbient />

        <div className="love-topbar">
          <AnimatePresence>
            {showReplay && (
              <motion.button
                type="button"
                className="love-pill"
                onClick={startOver}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="love-pill-icon">
                  <path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3M4.5 4.5v3.8h3.8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                start again
              </motion.button>
            )}
          </AnimatePresence>
          <Link to="/earth" className="love-pill love-pill--earth">
            view earth day
          </Link>
        </div>

        <motion.header className="love-header" layout="position" transition={layoutGlide}>
          <p className="love-eyebrow">for Sarvin</p>
          <motion.h1 className="love-title" layout="position" transition={layoutGlide}>
            happy four months
          </motion.h1>
          <ProgressHearts total={monthTotal + 1} filled={progressFilled} />
        </motion.header>

        <main className="love-stage">
          {finaleStage !== "card" && (
            <motion.div
              key={playRound}
              className="gift-column"
              initial={{ opacity: 0, scale: 0.85, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 160, damping: 18, delay: 0.15, layout: layoutGlide.layout }}
              layout="position"
            >
              <div ref={giftRef} className="gift-anchor">
                <GiftBox
                  mode={giftMode}
                  onOpen={handleGiftTap}
                  isReady={Boolean(months)}
                  label={hint || "gift"}
                />
              </div>
              <p className={`gift-hint${giftMode === "idle" && hint ? "" : " is-hidden"}`}>{hint}</p>
            </motion.div>
          )}

          {isFinale && (
            <motion.div
              key={`envelope-${playRound}`}
              className="envelope-rise"
              style={{ pointerEvents: finaleStage === "card" ? "auto" : "none" }}
              initial={{ opacity: 0, scale: 0.18, y: 30 }}
              animate={{ opacity: 1, scale: [0.18, 0.8, 1], y: [30, -40, 0] }}
              transition={{
                delay: 0.55,
                duration: 1.7,
                times: [0, 0.55, 1],
                ease: ["easeOut", [0.33, 0, 0.2, 1]],
                opacity: { delay: 0.55, duration: 0.45, ease: "easeOut" },
              }}
            >
              <Envelope
                backImageSrc={cardBack}
                backImageSrcPortrait={cardBackPortrait}
                letterText={message}
                onPhaseChange={handleEnvelopePhase}
              />
            </motion.div>
          )}
        </main>

        <AnimatePresence onExitComplete={handleCollageExitComplete}>
          {activeMonth && (
            <PhotoCollage
              key={`month-${activeMonth.monthNumber}-${playRound}`}
              month={activeMonth}
              entranceStyle={entranceStyles[activeMonthIndex % entranceStyles.length]}
              origin={giftOrigin}
              viewport={viewport}
              onOpenPhoto={(index) => setLightbox({ index, direction: 0 })}
              onClose={closeMonth}
            />
          )}
        </AnimatePresence>

        {burst && (
          <HeartBurst
            key={burst.key}
            variant={burst.variant}
            origin={burst.origin}
            viewport={viewport}
            seed={burst.key % 100000}
          />
        )}

        <AnimatePresence>
          {lightbox && activeMonth && (
            <Lightbox
              photos={activeMonth.photos}
              index={lightbox.index}
              direction={lightbox.direction}
              onNavigate={navigateLightbox}
              onClose={() => setLightbox(null)}
            />
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
};

export default Home;
